// Bank reconciliation engine v2 — daily anti-fraud control.
//
// Model: ONE movements table across all accounts. Bank lines are PARENTS; the
// itemized detail (Afirme TPV report, Clip API) are SUB-movements. We reconcile
// the subs/leaves 1:1 against the real payments the sellers entered; a parent
// auto-completes once all its subs are matched and the sum ties.
//
// Matching is multi-pass, "lock the certain then narrow":
//   0. assemble subs under parents (TPV by deposit-date + debit/credit)
//   1. RULES — fees / IVA / POS commissions / internal transfers auto-reconcile
//   2. UNIQUE keys — card by last4+amount+date+type, SPEI by RFC+amount+date
//   3. TRANSFER pairing — outflow on A ↔ inflow on B (auto-create the transfer)
//   4. PARENT auto-complete — all subs matched & sum ties
//   5. leftovers → suggest / exception (human or AI later)
//
// Pure functions; the component persists. "Humans verify everything for now" —
// these only PROPOSE (status auto_matched); a person flips verified.

export const CATEGORY = {
  INCOME: "income",
  CARD_SALE: "card_sale",
  CLIP: "clip_settlement",
  CASH_DEPOSIT: "cash_deposit",
  DEPOSIT_IN: "deposit_in",
  DEPOSIT_RETURN: "deposit_return",
  POS_COMMISSION: "pos_commission",
  BANK_FEE: "bank_fee",
  IVA: "iva",
  INTERNAL: "internal_transfer",
  SUPPLIER: "supplier",
  OTHER: "other",
};

export const CATEGORY_LABEL = {
  income: "Ingreso SPEI",
  card_sale: "Venta TPV",
  clip_settlement: "Liquidación Clip",
  cash_deposit: "Depósito efectivo",
  deposit_in: "Depósito en garantía",
  deposit_return: "Devolución garantía",
  pos_commission: "Comisión TPV",
  bank_fee: "Comisión bancaria",
  iva: "IVA comisión",
  internal_transfer: "Traspaso interno",
  supplier: "Pago a proveedor",
  other: "Otro",
};

// Credits we expect to tie to one of our payments (used for the exceptions view).
export const INCOME_LIKE = new Set([
  CATEGORY.INCOME, CATEGORY.CARD_SALE, CATEGORY.CLIP,
  CATEGORY.CASH_DEPOSIT, CATEGORY.DEPOSIT_IN,
]);

const EPS = 0.01;
const DATE_WINDOW = 7;          // ± days for an amount/date candidate
const FLAG_BUSINESS_DAYS = 3;   // unmatched recorded payment → suspicious after this

// ---------- date helpers ----------
const d = (iso) => new Date(`${String(iso).substring(0, 10)}T00:00:00Z`);
export const dayDiff = (a, b) => Math.round((d(a) - d(b)) / 86400000);
export const absDayDiff = (a, b) => Math.abs(dayDiff(a, b));

export function businessDaysBetween(from, to) {
  let s = d(from), e = d(to);
  const sign = e < s ? -1 : 1;
  if (sign < 0) { const t = s; s = e; e = t; }
  let n = 0;
  for (let cur = new Date(s); cur < e; cur.setUTCDate(cur.getUTCDate() + 1)) {
    const wd = cur.getUTCDay();
    if (wd !== 0 && wd !== 6) n++;
  }
  return sign * n;
}

// ---------- categorization ----------
export function categorize(mv) {
  const t = `${mv.shortConcept || ""} ${mv.description || ""} ${mv.concepto || ""}`.toUpperCase();
  const credit = !!mv.credit;
  if (/\bIVA\b/.test(t) && /COMIS|COMVTA|COM\b|TPV/.test(t)) return CATEGORY.IVA;
  if (/COMVTA|COM\.?VTA/.test(t)) return CATEGORY.POS_COMMISSION;       // card-terminal commission
  if (/COMISION|MANEJO DE CUENTA|BONIF/.test(t)) return CATEGORY.BANK_FEE;
  if (/VENTA\s*TPV/.test(t)) return CATEGORY.CARD_SALE;
  if (/CLIP|PAYCLIP/.test(t)) return CATEGORY.CLIP;
  if (/GARANTIA/.test(t)) return credit ? CATEGORY.DEPOSIT_IN : CATEGORY.DEPOSIT_RETURN;
  if (/DEVOLUCION|DEVOUCION/.test(t) && !credit) return CATEGORY.DEPOSIT_RETURN;
  if (/PAYU|GOOGLE|NEXOFLEET|SATELITAL|PUBLICIDAD|STR\*|\bGPS\b/.test(t)) return CATEGORY.SUPPLIER;
  if (/(GOMOVE|BBASE|BANBASE|BANCO BASE)\b/.test(t) && /TRANSFER|SPEI/.test(t)) return CATEGORY.INTERNAL;
  if (/EFECTIVO/.test(t) && credit) return CATEGORY.CASH_DEPOSIT;
  if (credit) return CATEGORY.INCOME;
  return CATEGORY.OTHER;
}

// ---------- dedupe key (idempotent re-import) ----------
// Per-type identity: SPEI by clave de rastreo; cards/others by date+amount+ref.
export function dedupeHash(accountId, mv) {
  const amt = (mv.credit ? mv.amount : -mv.amount).toFixed(2);
  const key = mv.claveRastreo || mv.auth || mv.reference || (mv.description || "").substring(0, 40);
  return [accountId, mv.source || "bank_file", String(mv.date).substring(0, 10), amt, key].join("|");
}

// ---------- rules ----------
// rule: { id, enabled, match_category, match_regex, match_account_id, direction, action }
export function ruleFor(node, rules = []) {
  const text = `${node.concepto || ""}`.toUpperCase();
  for (const r of rules) {
    if (r.enabled === false) continue;
    if (r.match_category && r.match_category !== node.category) continue;
    if (r.match_account_id && r.match_account_id !== node.account_id) continue;
    if (r.direction && r.direction !== node.direction) continue;
    if (r.match_regex) {
      try { if (!new RegExp(r.match_regex, "i").test(text)) continue; } catch (_) { continue; }
    }
    return r;
  }
  return null;
}

// ---------- TPV sub assembly ----------
// Each TPV report sale is a sub-movement; its parent is the batched bank VENTA
// line of the same deposit date and card type (TPVDB=debit, TPVCR=credit), whose
// amount = the group's sum. Returns sub nodes carrying `parentKey` (the bank
// line's local key) so the caller can wire parent_id after insert.
export function assembleTpvSubs(bankLines, tpvSales = []) {
  const ventas = bankLines
    .map((b, i) => ({ b, i }))
    .filter(({ b }) => categorize(b) === CATEGORY.CARD_SALE && b.credit);
  const typeOf = (b) => (/TPVCR/i.test(`${b.shortConcept} ${b.description}`) ? "credit" : "debit");

  // group sales by deposit date + card type
  const groups = {};
  for (const s of tpvSales) {
    const k = `${s.depositDate}|${s.cardType}`;
    (groups[k] = groups[k] || []).push(s);
  }
  const subs = [];
  const usedVenta = new Set();
  for (const k of Object.keys(groups)) {
    const [depDate, cardType] = k.split("|");
    const sum = groups[k].reduce((a, s) => a + s.amount, 0);
    // find an unmatched VENTA line of that date+type whose amount ≈ the group sum
    const hit = ventas.find(({ b, i }) =>
      !usedVenta.has(i) && typeOf(b) === cardType &&
      absDayDiff(b.date, depDate) <= 2 && Math.abs(b.amount - sum) < EPS);
    if (!hit) continue;                       // no batch line yet (or itemized differently)
    usedVenta.add(hit.i);
    for (const s of groups[k]) {
      subs.push({
        parentKey: hit.i, source: "tpv_report",
        date: s.saleDate, amount: s.amount, credit: true, card_type: s.cardType,
        card_last4: s.cardLast4 || null, auth: s.auth || null, terminal: s.terminal || null,
        concepto: `Venta ${s.brand || ""} ****${s.cardLast4 || ""} ${s.auth || ""}`.trim(),
        category: CATEGORY.CARD_SALE,
      });
    }
  }
  return { subs, ventaParentIdx: usedVenta };
}

// ---------- payment matcher (1:1, unique-keys first) ----------
// node: a leaf bank movement or a sub. pool items carry { id, source, amount, date,
//   accountType, cardType, card_last4, reservation, customer, billing_entity_type,
//   billing_entity_id, verified, used }.
export function matchPayment(node, pool, { rfcMap = {}, client = null } = {}) {
  const within = (r) => absDayDiff(r.date, node.date) <= DATE_WINDOW && Math.abs(r.amount - node.amount) < EPS;
  const free = pool.filter((r) => !r.used && within(r));

  // Card sub: exact on last4 + card type.
  if (node.category === CATEGORY.CARD_SALE) {
    const cards = free.filter((r) => r.accountType === "card" && (!node.card_type || !r.cardType || r.cardType === node.card_type));
    const exact = node.card_last4
      ? cards.filter((r) => r.card_last4 && r.card_last4 === node.card_last4) : [];
    if (exact.length === 1) return { match: exact[0], method: "card_last4", confidence: 1 };
    if (cards.length === 1) return { match: cards[0], method: "card_amount_date", confidence: 0.85 };
    if (cards.length > 1) return { ambiguous: cards };
    return null;
  }

  // SPEI / income by RFC → client's payment.
  if (client) {
    const belongs = free.filter((r) =>
      (r.billing_entity_type === client.entityType && r.billing_entity_id === client.entityId) ||
      (client.entityType === "customer" && r.customer === client.entityId));
    if (belongs.length === 1) return { match: belongs[0], method: "rfc", confidence: 1 };
    if (belongs.length > 1) return { ambiguous: belongs };
  }

  // Generic amount + date single candidate.
  if (free.length === 1) return { match: free[0], method: "amount_date", confidence: 0.7 };
  if (free.length > 1) return { ambiguous: free };
  return null;
}

// ---------- transfer pairing ----------
// Over the full multi-account movement set: an internal-transfer charge on one
// account pairs with an internal-transfer credit on another (same amount, ±date).
export function pairTransfers(movements) {
  const pairs = [];
  const used = new Set();
  const charges = movements.filter((m) => m.category === CATEGORY.INTERNAL && m.direction === "charge");
  const credits = movements.filter((m) => m.category === CATEGORY.INTERNAL && m.direction === "credit");
  for (const c of charges) {
    const hit = credits.find((x) =>
      !used.has(x.id) && x.account_id !== c.account_id &&
      Math.abs(x.amount - c.amount) < EPS && absDayDiff(x.movement_date, c.movement_date) <= DATE_WINDOW);
    if (hit) { used.add(hit.id); pairs.push({ from: c, to: hit }); }
  }
  return pairs;
}

// ---------- exceptions (the daily control surface) ----------
export function computeExceptions({ leaves, unusedIncome = [], unusedOutflow = [], today }) {
  const recordedNotInBank = [], cashNotDeposited = [];
  for (const r of [...unusedIncome, ...unusedOutflow]) {
    if (r.verified) continue;
    if (businessDaysBetween(r.date, today) < FLAG_BUSINESS_DAYS) continue;
    (r.accountType === "cash" ? cashNotDeposited : recordedNotInBank).push(r);
  }
  const inBankNotRecorded = leaves.filter(
    (r) => r.direction === "credit" && r.status === "unmatched" && INCOME_LIKE.has(r.category));
  const amountMismatch = leaves.filter((r) => r.flag === "amount_mismatch");
  return { recordedNotInBank, cashNotDeposited, inBankNotRecorded, amountMismatch };
}
